<script setup lang="ts">
import { ref } from 'vue'
import { VImg } from '@hatfieldmedia/hmcore'
import BaseButton from './BaseButton.vue'

const emit = defineEmits<{
  register: []
}>()

const isMenuOpen = ref(false)

const navLinks = [
  { label: 'Events', href: '#events' },
  { label: 'Services', href: '#services' },
  { label: 'Benefits', href: '#benefits' },
  { label: 'Testimonials', href: '#testimonials' },
  // { label: 'Contact', href: '#contact' },
]

function scrollTo(href: string) {
  const el = document.querySelector(href)
  el?.scrollIntoView({ behavior: 'smooth' })
  isMenuOpen.value = false
}

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

function closeMenu() {
  isMenuOpen.value = false
}

function handleRegister() {
  isMenuOpen.value = false
  emit('register')
}
</script>

<template>
  <nav class="nav">
    <div class="nav-top">
      <VImg src="media/images/logo.png" class="nav-logo" :width="144" :height="36" fit="fill" />
      <button class="hamburger" @click.stop="toggleMenu" aria-label="Toggle menu">
        <svg v-if="!isMenuOpen" width="22" height="18" viewBox="0 0 22 18" fill="none">
          <line
            x1="2"
            y1="3"
            x2="20"
            y2="3"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
          <line
            x1="2"
            y1="9"
            x2="20"
            y2="9"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
          <line
            x1="2"
            y1="15"
            x2="20"
            y2="15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none">
          <line
            x1="3"
            y1="3"
            x2="15"
            y2="15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
          <line
            x1="15"
            y1="3"
            x2="3"
            y2="15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
    <div class="nav-register">
      <BaseButton @click="handleRegister">Register Now</BaseButton>
    </div>

    <div class="nav-panel" :class="{ open: isMenuOpen }">
      <ul class="nav-links">
        <li v-for="link in navLinks" :key="link.href">
          <a :href="link.href" @click.prevent="scrollTo(link.href)">{{ link.label }}</a>
        </li>
      </ul>
    </div>

    <div v-if="isMenuOpen" class="nav-overlay" @click="closeMenu"></div>
  </nav>
</template>

<style scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(3, 9, 18, 0.95);
  backdrop-filter: blur(1rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 12px 16px;
  display: flex;
  align-items: center;
}

.nav-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

@media (min-width: 1024px) {
  .nav-top {
    width: 176px;
  }
}

.nav-logo {
  height: auto;
  width: 176px;
}

.hamburger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #fff;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.nav-register {
  margin-top: 12px;
}

.nav-register :deep(.base-btn) {
  width: 100%;
  justify-content: center;
  padding: 0.65rem 2rem;
  font-size: 0.85rem;
  display: none;
}

@media (min-width: 1024px) {
  .nav-register :deep(.base-btn) {
    display: inline-flex;
  }
}

.nav-panel {
  display: none;
}

.nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: -1;
}

/* ─── Desktop ─── */
@media (min-width: 1024px) {
  .nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 3rem;
  }

  .nav-top {
    flex: 0 0 auto;
  }

  .hamburger {
    display: none;
  }

  .nav-register {
    order: 2;
    margin-top: 0;
    flex: 0 0 auto;
  }

  .nav-register :deep(.base-btn) {
    width: auto;
  }

  .nav-panel {
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
    align-items: center;
  }

  .nav-overlay {
    display: none;
  }

  .nav-links {
    display: flex;
    gap: 32px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .nav-links a {
    color: var(--text);
    text-decoration: none;
    font-family: 'HND', sans-serif;
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    transition: color 0.25s;
  }

  .nav-links a:hover {
    color: var(--pink-bright);
  }
}

/* ─── Desktop slide-out panel (unused) ─── */
@media (max-width: 1023px) {
  .nav-panel {
    position: fixed;
    top: 0;
    left: 0;
    width: 80%;
    height: 100vh;
    background: rgba(3, 9, 18, 0.98);
    backdrop-filter: blur(1rem);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    z-index: 101;
    padding: 80px 24px 24px;
    display: block;
  }

  .nav-panel.open {
    transform: translateX(0);
  }

  .nav-links {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .nav-links li {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .nav-links a {
    display: block;
    color: var(--text);
    text-decoration: none;
    font-family: 'HND', sans-serif;
    font-size: 1rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 16px 0;
    transition: color 0.25s;
  }

  .nav-links a:hover {
    color: var(--pink-bright);
  }
}
</style>
